import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { addSeconds, differenceInMinutes, subSeconds, addMinutes } from 'date-fns'
import { combineLatest, empty, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { FeatureCollection, Point, Feature, Polygon, LineString } from 'geojson'
import { XyzTileModel } from '../models/xyz-tile.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { WarningDatasources, WarningTypes } from '../enums/warning-types.enum'
import { WarningDataModel } from '../models/warning-event.model'
import { cellArea, cellToBoundary, cellToLatLng } from 'h3-js'

@Injectable()
export class DataCustomerWarningService {
  private cancelPreviousRequests = new Subject<void>()
  // Stores live warning events as [points, lines, polygons]
  private warningEventsData: FeatureCollection<any, WarningDataModel>[] =
    this.createEmptyFeatureCollections()
  public isDataInitialized = false
  // Map storing historic warnings with 1-minute timesteps: timestamp -> [points, lines, polygons]
  historicWarningMap: Map<string, FeatureCollection<any, WarningDataModel>[]> = new Map()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  /**
   * Creates empty feature collections for points, lines, and polygons.
   */
  private createEmptyFeatureCollections(): FeatureCollection<any, WarningDataModel>[] {
    return [
      { type: 'FeatureCollection', features: [] }, // Points
      { type: 'FeatureCollection', features: [] }, // Lines
      { type: 'FeatureCollection', features: [] }, // Polygons
    ]
  }

  /**
   * Processes feature properties for both live and historic warnings.
   * Sets expireIn (minutes until expiry + 5 min buffer).
   * For historic data, pass referenceTime to calculate relative to that timestamp.
   */
  private processFeatureProperties(
    feature: Feature<any, WarningDataModel>,
    referenceTime: Date = new Date(),
  ) {
    feature.properties.expireIn = Math.abs(
      differenceInMinutes(referenceTime, addMinutes(new Date(feature.properties.expiryTime), 5)),
    )
  }

  /**
   * Converts a line or polygon feature into a point feature using its eventCoordinates.
   * Used for displaying warnings as points on the map.
   */
  private createPointFeature(
    feature: Feature<Point | LineString | Polygon, WarningDataModel>,
  ): Feature<Point, WarningDataModel> {
    feature.properties.featureId = feature.id?.toString() ?? ''
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: feature.properties.eventCoordinates as any,
      },
      id: feature.id,
      properties: feature.properties,
    }
  }

  /**
   * Loads warning events for either live or historic data, depending on parameters.
   * Populates warningEventsData (live) or historicWarningMap (historic).
   */
  async loadWarningEvents(
    warningTypes: WarningTypes[],
    currentDatasource: WarningDatasources,
    timeStamp?: Date,
    xyzTiles?: XyzTileModel[],
    mapBounds?: maplibregl.LngLatBounds,
  ) {
    if (this.appState.isLoading) return
    this.isDataInitialized = true
    this.warningEventsData = this.createEmptyFeatureCollections()

    // Live warnings (current map view)
    if (!timeStamp && xyzTiles) {
      await this.loadLiveWarningEvents(xyzTiles, warningTypes, currentDatasource)
    }
    // Historic warnings (for a specific timestamp and bounding box)
    else if (timeStamp && mapBounds) {
      await this.loadHistoricWarningEventsMap(mapBounds, warningTypes, timeStamp, currentDatasource)
    }
    return this
  }

  /**
   * Loads live warning events for the current map tiles.
   * Splits features into lines, polygons, and generates points from both.
   */
  private async loadLiveWarningEvents(
    xyzTiles: XyzTileModel[],
    warningTypes: WarningTypes[],
    currentDatasource: WarningDatasources,
  ) {
    // Request warnings for all visible tiles
    const warningEventsRequests$ = xyzTiles.map((tile) =>
      this.loadWarningEventsData(tile, warningTypes, currentDatasource),
    )
    const warningEvents$ = combineLatest(warningEventsRequests$)
    const warningEvents = await firstValueFrom(
      warningEvents$.pipe(takeUntil(this.cancelPreviousRequests)),
    )

    this.warningEventsData = this.createEmptyFeatureCollections()

    warningEvents.forEach((warnings) => {
      if (!warnings) return

      // Extract and process line features
      const lineFeatures = warnings.features.filter((f) => {
        this.processFeatureProperties(f, new Date())
        return f.geometry.type === 'LineString'
      })
      this.warningEventsData[1].features.push(...lineFeatures)

      // Extract and process polygon features (only non-cancelled)
      const polygonFeatures = warnings.features.filter((f) => {
        this.processFeatureProperties(f, new Date())
        return f.geometry.type === 'Polygon' && f.properties.isCancelled === false
      })
      this.warningEventsData[2].features.push(...polygonFeatures)
    })

    // Generate point features from lines and polygons for display
    const allFeatures = [
      ...this.warningEventsData[1].features,
      ...this.warningEventsData[2].features,
    ]
    this.warningEventsData[0].features = allFeatures.map((f) => this.createPointFeature(f))
  }

  /**
   * Loads historic warning events for a given bounding box and timestamp.
   * Splits features into lines, polygons, and generates points from both.
   * Stores results in historicWarningMap with 1-minute timesteps (30min before/after).
   */
  private async loadHistoricWarningEventsMap(
    mapBounds: maplibregl.LngLatBounds,
    warningTypes: WarningTypes[],
    timeStamp: Date,
    currentDatasource: WarningDatasources,
  ) {
    // Only load if zoom level is sufficient
    if (this.appState.getState().zoom! < this.config.history_min_zoom_level) return

    const warnings = await this.loadHistoricWarningEvents(
      mapBounds,
      warningTypes,
      timeStamp,
      currentDatasource,
    )

    // Process and split features
    const processedWarnings = this.processHistoricWarnings(warnings)
    // Generate 1-minute timesteps: 30 minutes before to 30 minutes after
    this.generateHistoricTimesteps(processedWarnings, timeStamp)
  }

  /**
   * Processes historic warnings and splits them into points, lines, and polygons.
   */
  private processHistoricWarnings(warnings: FeatureCollection<any, WarningDataModel>) {
    const points: FeatureCollection<any, WarningDataModel> = {
      type: 'FeatureCollection',
      features: [],
    }
    const lines: FeatureCollection<any, WarningDataModel> = {
      type: 'FeatureCollection',
      features: [],
    }
    const polygons: FeatureCollection<any, WarningDataModel> = {
      type: 'FeatureCollection',
      features: [],
    }

    // Split features and process properties
    // Note: expireIn will be recalculated per timestep in generateHistoricTimesteps
    warnings.features.forEach((feature) => {
      this.processFeatureProperties(feature, new Date())
      if (feature.geometry.type === 'LineString' && feature.properties.h3id === '') {
        lines.features.push(feature)
      } else if (
        feature.properties.h3id &&
        feature.properties.h3id !== '' &&
        feature.properties.isCancelled === false
      ) {
        feature.geometry.type = 'Polygon'
        // Convert H3 indexes to polygon coordinates using h3.h3SetToMultiPolygon
        feature.geometry.coordinates = [cellToBoundary(feature.properties.h3id, true)]
        polygons.features.push(feature)
      }
    })

    // Generate point features from lines and polygons
    const allFeatures = [...lines.features, ...polygons.features]
    points.features = allFeatures.map((f) => this.createPointFeature(f))

    return { points, lines, polygons }
  }

  /**
   * Generates 1-minute timesteps from 30 minutes before to 30 minutes after the given timestamp.
   * Stores filtered warning data for each timestep in historicWarningMap.
   * Recalculates expireIn for each timestep relative to that specific time.
   */
  private generateHistoricTimesteps(
    processedWarnings: {
      points: FeatureCollection<any, WarningDataModel>
      lines: FeatureCollection<any, WarningDataModel>
      polygons: FeatureCollection<any, WarningDataModel>
    },
    centerTimestamp: Date,
  ) {
    // Create timesteps: 30 minutes before to 30 minutes after (61 total steps)
    for (let i = -30; i <= 30; i++) {
      const timestep = addMinutes(centerTimestamp, i)
      const timestepKey = timestep.toISOString()

      // Filter warnings for this specific timestep
      const allWarnings = [
        processedWarnings.points,
        processedWarnings.lines,
        processedWarnings.polygons,
      ]
      const filteredWarnings = this.filterWarningsByTimestampAndVersion(allWarnings, timestep)

      // Recalculate expireIn for each feature relative to this timestep
      filteredWarnings.forEach((collection) => {
        collection.features.forEach((feature) => {
          this.processFeatureProperties(feature, timestep)
        })
      })

      // Store in historic map
      this.historicWarningMap.set(timestepKey, filteredWarnings)
    }
  }

  /**
   * Loads warning events for a single tile from the backend.
   */
  async loadWarningEventsData(
    xyzTiles: XyzTileModel,
    warningTypes: WarningTypes[],
    currentDatasource: WarningDatasources,
  ): Promise<FeatureCollection<any, WarningDataModel>> {
    const httpOptions = { headers: new HttpHeaders({ accept: 'json' }) }
    const warningType = warningTypes.join('&warningTypes=')
    const url = `${this.config.backend_url}rcs/warnings/${xyzTiles.z}/${xyzTiles.x}/${xyzTiles.y}?warningTypes=${warningType}&warningDatasource=${currentDatasource}`
    return await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, WarningDataModel>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequests)),
    )
  }

  /**
   * Loads historic warning events for a bounding box and time range from the backend.
   * Returns an array of objects with timespan and featureCollection.
   */
  async loadHistoricWarningEvents(
    boundingBox: maplibregl.LngLatBounds,
    warningTypes: WarningTypes[],
    timeStamp: Date,
    currentDatasource: WarningDatasources,
  ): Promise<FeatureCollection<any, WarningDataModel>> {
    // Calculate time range (±1 hour around timestamp)
    const timestampStart = subSeconds(timeStamp, 3600).toISOString()
    const timestampEnd = addSeconds(timeStamp, 3600).toISOString()
    const minLat = boundingBox.getSouth()
    const maxLat = boundingBox.getNorth()
    const minLon = boundingBox.getWest()
    const maxLon = boundingBox.getEast()
    const httpOptions = { headers: new HttpHeaders({ accept: 'json' }) }
    const url = new URL(`${this.config.backend_url}rcs/warnings/historic`)
    url.searchParams.set('minLat', minLat.toString())
    url.searchParams.set('maxLat', maxLat.toString())
    url.searchParams.set('minLon', minLon.toString())
    url.searchParams.set('maxLon', maxLon.toString())
    url.searchParams.set('timestampStart', timestampStart)
    url.searchParams.set('timestampEnd', timestampEnd)
    warningTypes.forEach((warningType) => url.searchParams.append('warningTypes', warningType))
    url.searchParams.append('warningDatasource', currentDatasource)
    return await firstValueFrom(
      this.http
        .get<FeatureCollection<any, WarningDataModel>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequests)),
    )
  }

  /**
   * Returns the raw warning events for the current view (live) or for a given timestamp (historic).
   * If no data is found for the timestamp, returns empty feature collections.
   * For historic data with timestamp, checks historicWarningMap first before filtering.
   */
  getRawWarningEvents(timestamp?: Date): FeatureCollection<any, WarningDataModel>[] {
    if (timestamp === undefined) {
      return this.warningEventsData
    } else {
      // Check if we have pre-computed data for this exact timestamp
      const timestampKey = timestamp.toISOString()
      if (this.historicWarningMap.has(timestampKey)) {
        return this.historicWarningMap.get(timestampKey)!
      } else {
        // Return empty feature collections if timestamp not found
        return this.createEmptyFeatureCollections()
      }
    }
  }

  /**
   * Filters warning features by timestamp (active during the time) and returns highest version by ID.
   */
  private filterWarningsByTimestampAndVersion(
    warningData: FeatureCollection<any, WarningDataModel>[],
    timestamp: Date,
  ): FeatureCollection<any, WarningDataModel>[] {
    const timestampMs = timestamp.getTime()

    return warningData.map((collection) => {
      // Filter features that are active during the timestamp
      const activeFeatures = collection.features.filter((feature) => {
        const startTime = new Date(feature.properties.startTime).getTime()
        const endTime = new Date(feature.properties.expiryTime).getTime()
        // For cancelled warnings, consider them active if timestamp is after startTime (they are cancelled but still relevant)
        if (feature.properties.isCancelled) {
          return timestampMs >= startTime
        } else {
          return timestampMs >= startTime && timestampMs <= endTime
        }
      })

      // Group by ID + segmentId and keep highest version per segment
      const groupedById = new Map<string, (typeof activeFeatures)[0]>()

      activeFeatures.forEach((feature) => {
        const uuid = Number(feature.properties.warningUUID)
        const segmentId = feature.properties.segmentId ?? ''
        const version = Number(feature.properties.version)
        const eventCoordinates = feature.properties.eventCoordinates
        const warningName = feature.properties.name
        const key = `${uuid}_${segmentId}_${eventCoordinates}_${warningName}`

        if (!groupedById.has(key)) {
          groupedById.set(key, feature)
        } else {
          const existingFeature = groupedById.get(key)!
          const existingVersion = Number(existingFeature.properties.version)

          // Compare versions (version is a number)
          if (version > existingVersion) {
            groupedById.set(key, feature)
          }
        }
      })
      // Filter grouped features to keep only those where timestampMs is within their time range
      // (Kick out cancellation events which arent valid for this timestamp
      // but have the highest version)
      const filteredFeatures = Array.from(groupedById.values()).filter((feature) => {
        const startTime = new Date(feature.properties.startTime).getTime()
        const endTime = new Date(feature.properties.expiryTime).getTime()
        return timestampMs >= startTime && timestampMs - 60000 <= endTime
      })

      return {
        type: 'FeatureCollection',
        features: filteredFeatures,
      }
    })
  }

  /**
   * Cancels all pending requests by emitting the destroy signal.
   */
  cancelCurrentRequests(): void {
    this.cancelPreviousRequests.next()
  }
}
